import { isAxiosError } from 'axios';

import { requestData, requestList } from '@/api/request';
import type { TenantUser, TenantUserPayload } from '@/api/types';

export const TENANT_USERS_MAX_PAGE_SIZE = 200;

export type GetTenantUsersOptions = {
  tenantId?: number;
  pageSize?: number;
};

export async function getTenantUsers({ tenantId, pageSize }: GetTenantUsersOptions = {}) {
  const resolvedPageSize = Math.min(
    Math.max(1, pageSize ?? TENANT_USERS_MAX_PAGE_SIZE),
    TENANT_USERS_MAX_PAGE_SIZE,
  );

  return requestList<TenantUser, never, { tenant?: number; page_size: number }>({
    url: 'tenant-users/',
    params: {
      ...(tenantId ? { tenant: tenantId } : {}),
      page_size: resolvedPageSize,
    },
  });
}

export async function createTenantUser(payload: TenantUserPayload) {
  return requestData<TenantUser, TenantUserPayload>({
    url: 'tenant-users/',
    method: 'post',
    data: payload,
  });
}

export async function getTenantUser(id: number) {
  return requestData<TenantUser>({
    url: `tenant-users/${id}/`,
  });
}

export async function updateTenantUser(id: number, payload: TenantUserPayload) {
  try {
    return await requestData<TenantUser, TenantUserPayload>({
      url: `tenant-users/${id}/`,
      method: 'put',
      data: payload,
    });
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;

    if (status !== 404 && status !== 405 && status !== 501) {
      throw error;
    }

    return requestData<TenantUser, TenantUserPayload>({
      url: `tenant-users/${id}/`,
      method: 'patch',
      data: payload,
    });
  }
}

export async function deleteTenantUser(id: number) {
  await requestData<void>({
    url: `tenant-users/${id}/`,
    method: 'delete',
  });
}